/**
 * AuthContext.jsx
 * Contexte React pour la gestion de l'authentification
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext(null);

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('gf_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 > Date.now()) {
          fetch(`${API_BASE}/me`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.ok ? r.json() : null)
            .then(u => {
              if (u && !u.error) {
                setUser(u);
              } else {
                // Fallback sur le payload JWT si /me échoue (ex: token formateur ou erreur serveur)
                setUser({ id: payload.id, login: payload.login, role: payload.role, nom: payload.nom || '', prenom: payload.prenom || '', session_id: payload.session_id || null, formateur_id: payload.formateur_id || null });
              }
            })
            .catch(() => setUser({ id: payload.id, login: payload.login, role: payload.role, nom: payload.nom || '', prenom: payload.prenom || '', session_id: payload.session_id || null, formateur_id: payload.formateur_id || null }));
        } else {
          logout();
        }
      } catch {
        logout();
      }
    }
    setLoading(false);
  // eslint-disable-next-line
  }, []);

  async function login(loginVal, password) {
    const res = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login: loginVal, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erreur de connexion');
    // Étape 2FA requise — on retourne les données sans connecter l'utilisateur
    if (data.requires_2fa) return data;
    await _finalizeLogin(data);
    return data.user;
  }

  async function refreshUser() {
    const t = localStorage.getItem('gf_token');
    if (!t) return;
    try {
      const r = await fetch(`${API_BASE}/me`, { headers: { Authorization: `Bearer ${t}` } });
      if (r.ok) { const u = await r.json(); if (u && !u.error) setUser(u); }
    } catch {}
  }

  async function _finalizeLogin(data) {
    localStorage.setItem('gf_token', data.token);
    setToken(data.token);
    setUser(data.user);
    try {
      const meRes = await fetch(`${API_BASE}/me`, { headers: { Authorization: `Bearer ${data.token}` } });
      if (meRes.ok) { const u = await meRes.json(); if (u && !u.error) setUser(u); }
    } catch {}
  }

  async function verify2fa(tempToken, code) {
    const res = await fetch(`${API_BASE}/auth/2fa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tempToken}` },
      body: JSON.stringify({ code }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Code invalide');
    await _finalizeLogin(data);
    return data.user;
  }

  async function sendSms2fa(tempToken) {
    const res = await fetch(`${API_BASE}/auth/2fa/send-sms`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${tempToken}` },
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erreur envoi SMS');
    return data;
  }

  async function verifySms2fa(tempToken, code) {
    const res = await fetch(`${API_BASE}/auth/2fa/verify-sms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tempToken}` },
      body: JSON.stringify({ code }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Code SMS invalide');
    await _finalizeLogin(data);
    return data.user;
  }

  // Connexion directe avec JWT déjà obtenu (ex : formateur via QR code + PIN)
  // Stocké dans gf_formateur_token (clé séparée) pour ne pas écraser le token de l'app normale
  function loginWithToken(jwtToken) {
    try {
      const payload = JSON.parse(atob(jwtToken.split('.')[1]));
      localStorage.setItem('gf_formateur_token', jwtToken);
      setToken(jwtToken);
      setUser({ id: payload.id, role: payload.role, nom: payload.nom || '', prenom: payload.prenom || '', session_id: payload.session_id, formateur_id: payload.formateur_id || null });
    } catch (e) { console.error('loginWithToken: JWT invalide', e); }
  }

  function logout() {
    const t = localStorage.getItem('gf_token');
    if (t) {
      fetch(`${API_BASE}/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { Authorization: `Bearer ${t}` },
      }).catch(() => {});
    }
    localStorage.removeItem('gf_token');
    localStorage.removeItem('gf_formateur_token');
    setToken(null);
    setUser(null);
  }

  // Rétrocompatibilité : vérifie le niveau minimum de rôle
  // admin > utilisateur > formateur = utilisateur > visiteur
  function hasRole(roleMin) {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (roleMin === 'admin') return false;
    // formateur a les mêmes droits que utilisateur dans l'app
    if (user.role === 'formateur' && (roleMin === 'utilisateur' || roleMin === 'visiteur')) return true;
    if (user.role === 'utilisateur' && roleMin === 'utilisateur') return true;
    if (roleMin === 'visiteur') return true;
    return false;
  }

  const isFormateur = user?.role === 'formateur';
  const formateurSessionId = user?.session_id || null;
  const formateurId = user?.formateur_id || null;

  // Retourne les permissions du rôle (objet ou null pour admin)
  function _getPerms() {
    if (!user) return null;
    if (user.role === 'admin') return null; // admin = tout
    const perms = user.role_permissions;
    // Fallback défensif : si le rôle "utilisateur" n'a pas de permissions définies
    // (BDD mal initialisée), accès complet par défaut
    if (user.role === 'utilisateur' && (!perms || Object.keys(perms).length === 0)) {
      return { '*': { mode: 'modification' } };
    }
    return perms || {};
  }

  // Vérifie si l'utilisateur peut accéder à une vue
  function canAccess(vue) {
    if (!user) return false;
    const perms = _getPerms();
    if (perms === null) return true; // admin
    if ('*' in perms) return true;   // accès complet défini sur le rôle
    return vue in perms;
  }

  // Vérifie si l'utilisateur peut modifier (pas seulement consulter)
  // onglet optionnel — si fourni, vérifie le droit spécifique à cet onglet
  function canEdit(vue, onglet = null) {
    if (!user) return false;
    const perms = _getPerms();
    if (perms === null) return true; // admin

    // Accès complet via wildcard
    if ('*' in perms) {
      if (onglet) {
        // Les onglets ne peuvent pas surcharger le wildcard (accès global)
        return perms['*'].mode === 'modification';
      }
      return perms['*'].mode === 'modification';
    }

    const vueConfig = perms[vue];
    if (!vueConfig) return false;

    if (onglet && vueConfig.onglets && vueConfig.onglets[onglet]) {
      return vueConfig.onglets[onglet] === 'modification';
    }
    return vueConfig.mode === 'modification';
  }

  // Vérifie si un onglet spécifique est visible (non masqué par 'aucun')
  function canAccessOnglet(vue, onglet) {
    if (!user) return false;
    const perms = _getPerms();
    if (perms === null) return true;
    if ('*' in perms) return true;
    const vueConfig = perms[vue];
    if (!vueConfig) return false;
    if (vueConfig.onglets && vueConfig.onglets[onglet] === 'aucun') return false;
    return true;
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, login, loginWithToken, logout, hasRole, canAccess, canEdit, canAccessOnglet, isFormateur, formateurSessionId, formateurId, verify2fa, sendSms2fa, verifySms2fa, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
